import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Chip,
  List,
  Alert,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { stockService } from '../../../services/stockService';
import type { Stock, StockProductDistribution } from '../../../types/stock';

export default function StockDetailPage() {
  const { id, stockId: stockIdParam } = useParams<{ id: string; stockId: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;
  const stockId = stockIdParam ? parseInt(stockIdParam) : null;

  // Data
  const [stock, setStock] = useState<Stock | null>(null);
  const [productDistribution, setProductDistribution] = useState<StockProductDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!companyId || !stockId) {
        setError('Ідентифікатор складу відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [stockData, availability] = await Promise.all([
          stockService.getById(stockId),
          stockService.getAvailability(companyId, [stockId]),
        ]);
        if (cancelled) {
          return;
        }
        setStock(stockData);
        setProductDistribution(availability[0]?.productDistribution ?? []);
        setError(null);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Error fetching stock details:', err);
        setError('Не вдалося завантажити дані складу');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [companyId, stockId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !stock) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error ?? 'Склад не знайдено'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/stocks`)}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          {stock.name}
        </Typography>
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Продукти на складі
      </Typography>

      {productDistribution.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            textAlign: 'center',
            backgroundColor: 'background.default',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            На цьому складі ще немає жодного продукту
          </Typography>
        </Paper>
      ) : (
        <List>
          {productDistribution.map((dist) => (
            <Paper
              key={dist.productId}
              elevation={1}
              sx={{
                mb: 2,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory2Icon sx={{ color: 'text.secondary' }} />
                <Typography variant="body1">{dist.productName}</Typography>
              </Box>
              <Chip
                label={`${dist.quantity} шт`}
                size="small"
                color={dist.quantity > 0 ? 'success' : 'default'}
                variant="outlined"
              />
            </Paper>
          ))}
        </List>
      )}
    </Box>
  );
}
